import { EventApi, EventContentArg, MoreLinkArg } from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';
import enGbLocale from '@fullcalendar/core/locales/en-gb';
import esLocale from '@fullcalendar/core/locales/es';
import nlLocale from '@fullcalendar/core/locales/nl';
import InteractionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import TimeGridPlugin from '@fullcalendar/timegrid';
import { Locales } from '@griply/types';
import { addDays, endOfDay, startOfDay, startOfWeek } from 'date-fns';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FullCalendarEvent } from 'shared/components/ui/full-calendar-event';
import { FullCalendarMorePopover } from 'shared/components/ui/full-calendar-more-popover';
import { useToday } from 'shared/contexts/today';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { eventAllow } from 'shared/lib/full-calendar/event-allow';
import { eventOrder } from 'shared/lib/full-calendar/event-order';
import { getEventTimeText } from 'shared/lib/full-calendar/get-event-time-text';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { CalendarTaskSync } from 'shared/types/calendar-task-sync';
import {
  fullCalendarMilitaryTime,
  fullCalendarStandardTime,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { SmartList } from 'shared/types/smart-list';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import { DateHeader } from './date-header';
import * as Styled from './full-calendar-week.style';

const plugins = [TimeGridPlugin, InteractionPlugin];
const supportedLocales = [nlLocale, enGbLocale, esLocale, deLocale];
const localeMap: Record<Locales, string> = {
  [Locales.NlNL]: 'nl',
  [Locales.EnUS]: 'en-gb',
  [Locales.EsES]: 'es',
  [Locales.DeDE]: 'de',
};

export type FullCalendarWeekProps = Omit<
  React.ComponentProps<typeof FullCalendar>,
  | 'plugins'
  | 'initialView'
  | 'dayHeaderContent'
  | 'eventAllow'
  | 'contentHeight'
> & {
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  smartLists: SmartList[];
  onCompleteTask: (id: ID, date: Timestamp) => void;
  onClickEvent?: (id: ID) => void;
  rangeReferenceDate?: Timestamp;
  rangeDays?: number;
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  calendarSync?: CalendarTaskSync;
};

export const FullCalendarWeek = forwardRef<
  HTMLDivElement,
  FullCalendarWeekProps
>(
  (
    {
      tasks,
      goals,
      lifeAreas,
      tags,
      smartLists,
      onCompleteTask,
      onClickEvent,
      rangeReferenceDate,
      rangeDays = 7,
      fullCalendarRef,
      events,
      timeFormat,
      weekStartsOn,
      calendarSync,
      ...rest
    },
    ref,
  ) => {
    const today = useToday();
    const locale = useI18nLocale();
    const _fullCalendarRef = useForwardedRef(fullCalendarRef);
    const containerRef = useForwardedRef<HTMLDivElement>(ref);
    const [moreInfo, setMoreInfo] = useState<{
      position: { top: number; left: number };
      events: EventApi[];
    }>();

    const onMore = useCallback(({ jsEvent, hiddenSegs }: MoreLinkArg) => {
      jsEvent.preventDefault();
      setMoreInfo({
        position: {
          top: (jsEvent as MouseEvent).clientY,
          left: (jsEvent as MouseEvent).clientX,
        },
        events: hiddenSegs.map(({ event }) => event),
      });

      return '';
    }, []);
    const closeMore = useCallback(() => setMoreInfo(undefined), []);

    const _onClickEvent = useCallback(
      (id: ID) => {
        onClickEvent?.(id);
        closeMore();
      },
      [closeMore, onClickEvent],
    );

    const renderEvent = useCallback(
      ({ event, timeText, isStart, isEnd, isPast }: EventContentArg) => (
        <FullCalendarEvent
          event={event}
          timeText={getEventTimeText(timeText, {
            allDay: event.allDay,
            isStart,
            isEnd,
            start: event.start,
            timeFormat,
          })}
          isPast={isPast}
          tasks={tasks}
          goals={goals}
          lifeAreas={lifeAreas}
          tags={tags}
          smartLists={smartLists}
          onComplete={onCompleteTask}
          onClick={_onClickEvent}
          calendarSync={calendarSync}
        />
      ),
      [
        timeFormat,
        tasks,
        goals,
        lifeAreas,
        tags,
        smartLists,
        onCompleteTask,
        _onClickEvent,
        calendarSync,
      ],
    );

    const visibleRange = useMemo(
      () => ({
        start: startOfDay(rangeReferenceDate ?? startOfWeek(today)),
        end: endOfDay(addDays(rangeReferenceDate ?? today, rangeDays - 1)),
      }),
      [rangeDays, rangeReferenceDate, today],
    );

    useEffect(() => {
      // if events have updated, close the more list
      closeMore();
      // eslint-disable-next-line react-hooks/exhaustive-deps -- only when events are updated we close the more
    }, [events]);

    return (
      <Styled.Container ref={containerRef}>
        <FullCalendar
          ref={_fullCalendarRef}
          plugins={plugins}
          initialView="timeGrid"
          slotDuration="00:15:00"
          slotLabelInterval="01:00:00"
          visibleRange={visibleRange}
          dayHeaderContent={DateHeader}
          eventAllow={eventAllow}
          height="100%"
          locales={supportedLocales}
          locale={localeMap[locale]}
          nowIndicator
          stickyHeaderDates
          eventResizableFromStart
          eventShortHeight={32}
          dayMaxEvents={4}
          moreLinkClick={onMore}
          eventContent={renderEvent}
          eventOrder={eventOrder}
          scrollTimeReset={false}
          events={events}
          slotLabelFormat={
            timeFormat === TimeFormatOptions.Standard
              ? fullCalendarStandardTime
              : fullCalendarMilitaryTime
          }
          eventTimeFormat={
            timeFormat === TimeFormatOptions.Standard
              ? fullCalendarStandardTime
              : fullCalendarMilitaryTime
          }
          displayEventEnd
          allDaySlot
          {...rest}
        />

        {moreInfo && (
          <FullCalendarMorePopover
            events={moreInfo.events}
            location={moreInfo.position}
            onOutside={closeMore}
            weekStartsOn={weekStartsOn}
            tasks={tasks}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            smartLists={smartLists}
            onComplete={onCompleteTask}
            onClick={_onClickEvent}
            calendarSync={calendarSync}
          />
        )}
      </Styled.Container>
    );
  },
);
